import { useCallback, useEffect, useState } from 'react';
import {
    Alert, Button, Collapse, Descriptions, Flex, Modal, Radio, Space, Spin, Switch,
    Statistic, Table, Tag, Tooltip, Typography, message,
} from 'antd';
import { ReloadOutlined, WifiOutlined } from '@ant-design/icons';
import { useRecoilValue } from 'recoil';
import { themeState } from '../../atom';
import CustomCard from '../../components/Card';
import SettingsApiService from '../../APIServices/SettingsApiService';

const { Title, Text, Paragraph } = Typography;

/**
 * Panel settings. One decision: which provider fulfils an eSIM sale.
 *
 * THE PAGE IS THE CHOICE, AND NOTHING ELSE ABOVE THE FOLD. It used to open on
 * the choice plus three blocks of evidence — a routing preview with three
 * counters, a dozen reason tags and a six-column cost table, then a Telna
 * readiness panel of four more counters and a four-row table. All of it true,
 * none of it needed to answer "who fulfils our eSIMs?", and together it buried
 * the one control under a page of numbers.
 *
 * So the evidence is still here, one click down, under its own headline. The
 * rule: anything that changes the decision is on the page, anything that
 * merely explains it is in the panel.
 *
 * THE PREVIEW IS NOW FETCHED ONLY WHEN OPENED. It prices ~3,180 plans against
 * the rate card on the server; doing that on every page view spent that work
 * on a page most people open to read one radio button.
 */

/** Why a plan stayed on eSIM Access. Phrased as the fix, not as the code. */
const REASON_LABELS = {
    cheaper_on_telna: 'Routed to Telna — cheaper',
    mode_telna: 'Routed to Telna — Telna-only mode',
    mode_esimaccess: 'eSIM Access — mode is eSIM Access only',
    imsi_exposure_too_high: 'Blocked — the IMSI profiles differ too much in price',
    worst_case_cost_too_high: 'Blocked — worst-case cost per sale too high',
    not_cheaper_than_esimaccess: 'eSIM Access is cheaper',
    no_telna_rate: 'Telna has no rate for one of the countries',
    no_esimaccess_cost: 'No eSIM Access cost to compare against',
    telna_purchases_disabled: 'Preferred Telna, but purchases are switched off',
    telna_out_of_stock: 'Preferred Telna, but no unsold SIMs',
    telna_unreachable: 'Preferred Telna, but Telna is unreachable',
    telna_not_configured: 'Preferred Telna, but no API key is set',
};

const Settings = () => {
    const theme = useRecoilValue(themeState);

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    /** Separate from `saving`: the preview toggle and the provider radio can
     *  be in flight at once and must not disable each other. */
    const [savingPreview, setSavingPreview] = useState(false);

    const [previewMode, setPreviewMode] = useState('auto');
    const [preview, setPreview] = useState(null);
    const [previewing, setPreviewing] = useState(false);
    const [detailOpen, setDetailOpen] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            setData(await SettingsApiService.getEsimProvider());
        } catch (error) {
            message.error(error.message || 'Failed to load settings.');
        } finally {
            setLoading(false);
        }
    }, []);

    const loadPreview = useCallback(async (mode) => {
        setPreviewing(true);
        try {
            setPreview(await SettingsApiService.getRoutingPreview(mode));
        } catch (error) {
            message.error(error.message || 'Failed to build the preview.');
            setPreview(null);
        } finally {
            setPreviewing(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // Only once the panel is open, and again when the mode inside it changes.
    useEffect(() => {
        if (detailOpen) loadPreview(previewMode);
    }, [detailOpen, previewMode, loadPreview]);

    /**
     * Switching is a commercial change with no undo for orders placed in
     * between, so it asks first. The confirmation spells out what moves and
     * what does not rather than asking "are you sure?" — which is also where
     * "new sales only" belongs, at the moment it matters.
     */
    const confirmSwitch = (mode) => {
        const target = (data?.modes || []).find((m) => m.value === mode);
        Modal.confirm({
            title: `Route new eSIM sales using "${target?.label || mode}"?`,
            content: (
                <>
                    <Paragraph style={{ marginBottom: 8 }}>{target?.description}</Paragraph>
                    <Paragraph style={{ marginBottom: 0 }} type="secondary">
                        Orders already placed keep their original provider and are unaffected,
                        including ones still waiting for delivery.
                    </Paragraph>
                </>
            ),
            okText: 'Save',
            cancelText: 'Cancel',
            onOk: () => save(mode),
        });
    };

    const save = async (provider) => {
        setSaving(true);
        try {
            const res = await SettingsApiService.setEsimProvider(provider);
            message.success(res?.message || 'Saved.');
            await load();
            if (detailOpen) await loadPreview(provider === 'esimaccess' ? 'auto' : provider);
        } catch (error) {
            // The 409 carries the reasons the switch was refused. Showing
            // "Failed to save" instead threw away the only part the operator
            // could act on.
            const why = error.blockers?.length ? ` ${error.blockers.join(' ')}` : '';
            message.error(`${error.message || 'Failed to save.'}${why}`);
        } finally {
            setSaving(false);
        }
    };

    /**
     * The per-megabyte store's test door.
     *
     * DELIBERATELY NOT THE PROVIDER SWITCH. That one decides what every
     * customer gets when they tap eSIM; this decides only whether somebody
     * who types /new-esims may see it. Without the separation the only way to
     * try Telna was to point the whole shop at it.
     */
    const togglePreview = async (enabled) => {
        setSavingPreview(true);
        try {
            await SettingsApiService.setPerMbPreview(enabled);
            setData((d) => ({ ...d, perMbPreview: enabled }));
            message.success(enabled
                ? '/new-esims is now reachable directly'
                : '/new-esims is closed again');
        } catch (error) {
            message.error(error.message || 'Could not change that setting.');
        } finally {
            setSavingPreview(false);
        }
    };

    if (loading && !data) {
        return (
            <CustomCard theme={theme}>
                <Flex justify="center" style={{ padding: 48 }}><Spin /></Flex>
            </CustomCard>
        );
    }

    const telna = data?.readiness?.telna || {};
    const previewOn = data?.perMbPreview === true;
    const current = data?.provider;

    /**
     * The one fact that decides whether this page does anything today.
     *
     * Purchases are enabled in the environment, not here, because attaching a
     * Telna package spends prepaid balance and Telna has no refund call. While
     * that is off, every sale is fulfilled by eSIM Access whatever is selected
     * — so the page says so at the top instead of leaving someone to find it
     * in a footnote after switching.
     */
    const inert = !telna.provisioningEnabled && current !== 'esimaccess';

    const exampleColumns = [
        { title: 'Plan', dataIndex: 'name', key: 'name', render: (v) => <Text style={{ fontSize: 12 }}>{v}</Text> },
        {
            title: 'Retail',
            dataIndex: 'retailEur',
            key: 'retailEur',
            render: (v) => `€${Number(v).toFixed(2)}`,
        },
        {
            title: <Tooltip title="What eSIM Access charges us for this plan">eSIM Access cost</Tooltip>,
            dataIndex: 'esimAccessCostUsd',
            key: 'ea',
            render: (v) => `$${Number(v).toFixed(2)}`,
        },
        {
            title: <Tooltip title="Telna bills consumption, not the allowance, so this assumes a typical traveller does not use every byte">Telna expected</Tooltip>,
            dataIndex: 'telnaExpectedUsd',
            key: 'exp',
            render: (v) => `$${Number(v).toFixed(2)}`,
        },
        {
            title: <Tooltip title="Every byte used, on the dearest IMSI profile we hold. The most one sale can cost us.">Worst case</Tooltip>,
            dataIndex: 'telnaCeilingUsd',
            key: 'ceil',
            render: (v) => <Text type={Number(v) > 10 ? 'danger' : undefined}>${Number(v).toFixed(2)}</Text>,
        },
        {
            title: 'Saving',
            dataIndex: 'savingUsd',
            key: 'saving',
            render: (v) => <Text strong style={{ color: '#237804' }}>${Number(v).toFixed(2)}</Text>,
        },
    ];

    const detail = (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Flex justify="space-between" align="center" wrap gap={12}>
                <Text type="secondary" style={{ fontSize: 13 }}>
                    What a mode would do to the catalogue. Read-only — nothing is purchased.
                </Text>
                <Radio.Group
                    size="small"
                    value={previewMode}
                    onChange={(e) => setPreviewMode(e.target.value)}
                    options={[{ value: 'auto', label: 'Auto' }, { value: 'telna', label: 'Telna only' }]}
                    optionType="button"
                />
            </Flex>

            {previewing && <Flex justify="center" style={{ padding: 24 }}><Spin /></Flex>}

            {!previewing && preview && (
                <>
                    <Flex gap={32} wrap>
                        <Statistic title="Plans in the shop" value={preview.totalPlans} />
                        <Statistic
                            title="Would use Telna"
                            value={preview.wouldRouteToTelna}
                            valueStyle={{ color: preview.wouldRouteToTelna ? '#237804' : undefined }}
                        />
                        <Statistic title="Would use eSIM Access" value={preview.wouldRouteToEsimAccess} />
                    </Flex>

                    <div>
                        {Object.entries(preview.reasons || {})
                            .sort((a, b) => b[1] - a[1])
                            .map(([reason, count]) => (
                                <Tag key={reason} style={{ marginBottom: 6 }}>
                                    {REASON_LABELS[reason] || reason}: <b>{count}</b>
                                </Tag>
                            ))}
                    </div>

                    {preview.examples?.length > 0 && (
                        <Table
                            size="small"
                            rowKey="packageCode"
                            columns={exampleColumns}
                            dataSource={preview.examples}
                            pagination={{ pageSize: 10, size: 'small' }}
                            scroll={{ x: true }}
                        />
                    )}
                </>
            )}

            <Descriptions size="small" column={1} bordered title="Telna">
                <Descriptions.Item label="API key configured">
                    {telna.configured ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag>}
                </Descriptions.Item>
                <Descriptions.Item label="Reachable">
                    {telna.reachable === null
                        ? <Tag>Not checked</Tag>
                        : telna.reachable ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag>}
                </Descriptions.Item>
                <Descriptions.Item label="Purchases enabled">
                    {telna.provisioningEnabled
                        ? <Tag color="green">Enabled</Tag>
                        : <Tag color="orange">Disabled — set in .env, not here</Tag>}
                </Descriptions.Item>
                <Descriptions.Item label="SIMs held / claimed">
                    {telna.stock?.sellable ?? '—'} / {telna.stock?.claimedLocally ?? '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Wallet balance">
                    {telna.wallet?.balance ?? '—'}{' '}
                    {telna.wallet?.type && (
                        <Text type="secondary" style={{ fontSize: 12 }}>{telna.wallet.type}</Text>
                    )}
                </Descriptions.Item>
                <Descriptions.Item label="Rate card">
                    <Text style={{ fontSize: 12 }}>
                        {telna.rateTable?.countryCount ?? '—'} countries · IMSI profiles{' '}
                        {(telna.rateTable?.heldProfiles || []).join(', ') || '—'} · assumes{' '}
                        {Math.round((telna.rateTable?.utilisation || 0) * 100)}% of each bundle is used
                    </Text>
                </Descriptions.Item>
            </Descriptions>
        </Space>
    );

    return (
        <CustomCard theme={theme}>
            <Flex justify="space-between" align="flex-start" wrap gap={16}>
                <div>
                    <Title level={4} style={{ margin: 0 }}>
                        <WifiOutlined style={{ marginRight: 8 }} />
                        eSIM provider
                    </Title>
                    <Text type="secondary">
                        Who fulfils a new eSIM sale on youguide.com. Orders already placed
                        keep the provider they were sold under.
                    </Text>
                </div>
                <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>
            </Flex>

            {inert && (
                <Alert
                    style={{ marginTop: 16 }}
                    type="warning"
                    showIcon
                    message="Telna purchases are switched off, so every sale is fulfilled by eSIM Access"
                    description={(
                        <>
                            <p style={{ margin: '0 0 8px' }}>
                                Enabled in the environment rather than here on purpose: attaching a
                                Telna package spends prepaid balance and Telna has no refund call.
                                This setting takes effect the moment purchases are turned on.
                            </p>
                            <p style={{ margin: '0 0 6px' }}>
                                <strong>To turn it on:</strong> set{' '}
                                <code>TELNA_PROVISIONING_ENABLED=true</code> in the API&rsquo;s
                                environment and redeploy. It must be the exact word{' '}
                                <code>true</code> &mdash; <code>1</code> and <code>yes</code> both
                                read as off.
                            </p>
                            <p style={{ margin: 0 }}>
                                Until then the per-megabyte shop stays closed to buyers and the
                                website keeps sending eSIM customers to the eSIM Access store, which
                                is what is actually delivering.
                            </p>
                        </>
                    )}
                />
            )}

            <Radio.Group
                style={{ marginTop: 20, width: '100%' }}
                value={current}
                disabled={saving}
                onChange={(e) => confirmSwitch(e.target.value)}
            >
                <Space direction="vertical" style={{ width: '100%' }} size={12}>
                    {(data?.modes || []).map((m) => {
                        const isCurrent = current === m.value;
                        return (
                            <Radio
                                key={m.value}
                                value={m.value}
                                style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    padding: 14,
                                    border: `1px solid ${isCurrent ? '#1677ff' : '#f0f0f0'}`,
                                    borderRadius: 8,
                                    width: '100%',
                                }}
                            >
                                <Space direction="vertical" size={2}>
                                    <Space>
                                        <Text strong>{m.label}</Text>
                                        {isCurrent && <Tag color="blue">Active</Tag>}
                                    </Space>
                                    <Text type="secondary" style={{ fontSize: 13 }}>{m.description}</Text>
                                </Space>
                            </Radio>
                        );
                    })}
                </Space>
            </Radio.Group>

            {/* Backend warnings. Kept on the page: they are reasons the choice
                above may not behave as read. */}
            {/*
              * The provisioning advisory is deliberately dropped here: the
              * banner above already says it, in full, with the fix. Two
              * warnings one screen apart saying the same thing made the page
              * look like it had two separate problems.
              */}
            {(data?.advisories || [])
                .filter((a) => !a.includes('TELNA_PROVISIONING_ENABLED') || !inert)
                .map((a) => (
                    <Alert key={a} style={{ marginTop: 10 }} type="warning" showIcon message={a} />
                ))}

            {data?.audit?.updatedAt && (
                <Text type="secondary" style={{ display: 'block', marginTop: 12, fontSize: 12 }}>
                    Last changed {new Date(data.audit.updatedAt).toLocaleString()}
                    {data.audit.updatedBy?.email ? ` by ${data.audit.updatedBy.email}` : ''}
                </Text>
            )}

            {/* A TESTING DOOR, not a sales switch. Placed after the provider
                choice and visually quieter, because turning it on changes
                nothing for a customer who does not type the URL. */}
            <div style={{
                marginTop: 20, padding: '14px 16px',
                border: '1px dashed rgba(128,128,128,0.4)', borderRadius: 10,
            }}>
                <Flex justify="space-between" align="start" gap={16} wrap>
                    <div style={{ maxWidth: 560 }}>
                        <Text strong>Open the per-megabyte store for testing</Text>
                        <div>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                Lets <Text code style={{ fontSize: 11 }}>/new-esims</Text> be
                                opened directly while the shop is still selling eSIM Access
                                bundles. Navigation is unchanged — customers who tap
                                <b> eSIM</b> still go to the bundles — and the page says it is
                                a preview. Buying is unaffected: checkout still refuses when
                                Telna cannot deliver or stock has run out.
                            </Text>
                        </div>
                    </div>
                    <Switch
                        checked={previewOn}
                        loading={savingPreview}
                        onChange={togglePreview}
                        checkedChildren="open"
                        unCheckedChildren="closed"
                    />
                </Flex>
            </div>

            <Collapse
                ghost
                style={{ marginTop: 16 }}
                onChange={(keys) => setDetailOpen(keys.includes('detail'))}
                items={[{
                    key: 'detail',
                    label: <Text strong>Routing detail and Telna status</Text>,
                    children: detail,
                }]}
            />
        </CustomCard>
    );
};

export default Settings;
